/**
 * BaaS home KPI metrics.
 * Pix / EFX / Stables rails are live (statements + stables transactions).
 * Credit + card count home KPIs aggregate live credit_summary / card_totals from GET /cards/programs.
 * Cards volume chart stays at zero until a dedicated cards volume / authorization endpoint ships.
 * Program detail binds live credit_summary + card_totals from GET /cards/programs/{id}.
 */

import { getMockBaasApiMetrics } from "../../../../Services/baasApiMonitoring";
import { getMockBaasCardPrograms } from "../../../../Services/baasCardProgramsApi";
import { convertBaasHomeAmountFromBrl, normalizeBaasHomeCurrency } from "./baasHomeCurrency";
import {
  getDefaultProcessingDateRange,
  getProcessingRangeDayCount,
  getProcessingSparklineGranularity,
  getProcessingVolumeMultiplier,
} from "./baasHomeProcessingDateFilters";

const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

const roundRate = (value) => Math.round((Number(value) || 0) * 100) / 100;

/** Empty card KPI shape (zeros when no IssuerCard rows). */
export const EMPTY_BAAS_HOME_CARDS_METRICS = {
  totalCards: 0,
  activeCards: 0,
  activationRate: 0,
  approvalRate: 0,
  chargebackRatio: 0,
};

/**
 * Aggregate program `cardTotals` into instance-level issuer card KPIs.
 * Sums total/active; recomputes activation_rate = active/total*100.
 * Approval / chargeback stay 0 until processor data exists.
 *
 * @param {Array<object|null|undefined>} programs UI programs from fetchBaasCardPrograms
 */
export const aggregateBaasProgramsCardsMetrics = (programs = []) => {
  const list = Array.isArray(programs) ? programs : [];
  let totalCards = 0;
  let activeCards = 0;

  for (const program of list) {
    const ct = program?.cardTotals;
    if (ct) {
      totalCards += Number(ct.totalCards) || 0;
      activeCards += Number(ct.activeCards) || 0;
      continue;
    }
    totalCards += Number(program?.issuancesCount) || 0;
  }

  const activationRate =
    totalCards > 0 ? Math.round((activeCards / totalCards) * 1000) / 10 : 0;

  return {
    totalCards,
    activeCards,
    activationRate,
    approvalRate: 0,
    chargebackRatio: 0,
  };
};

/** Empty credit KPI shape (zeros when no issuer accounts / no programs). */
export const EMPTY_BAAS_HOME_CREDIT_METRICS = {
  currency: "BRL",
  totalLimitBrl: 0,
  runningCreditBrl: 0,
  utilizationRate: 0,
  unsecuredGrantedBrl: 0,
  netExposureBrl: 0,
  collateralDepositedBrl: 0,
  runningCollateralizedBrl: 0,
  runningUnsecuredBrl: 0,
  availableLimitBrl: 0,
  collateralizedRate: 0,
  unsecuredRate: 0,
  grantedLimitBrl: 0,
  reserveHeldBrl: 0,
  plannedCreditAmount: 0,
  issuerAccountCount: 0,
};

/**
 * Aggregate program `creditSummary` rows into instance-level credit KPIs.
 * Matches Baas BuildCreditSummary semantics across programs (sums + recomputed rates).
 * Returns zeros when there are no issuer accounts.
 *
 * @param {Array<object|null|undefined>} programs UI programs from fetchBaasCardPrograms
 * @param {string} [currency="BRL"]
 */
export const aggregateBaasProgramsCreditMetrics = (programs = [], currency = "BRL") => {
  const displayCurrency = normalizeBaasHomeCurrency(currency);
  const list = Array.isArray(programs) ? programs : [];
  const convert = (amountBrl) => convertBaasHomeAmountFromBrl(amountBrl, displayCurrency);

  let totalLimit = 0;
  let runningCredit = 0;
  let grantedLimit = 0;
  let reserveHeld = 0;
  let plannedCredit = 0;
  let issuerAccountCount = 0;

  for (const program of list) {
    const cs = program?.creditSummary;
    if (cs) {
      totalLimit += Number(cs.totalLimitBrl) || 0;
      runningCredit += Number(cs.runningCreditBrl) || 0;
      grantedLimit += Number(cs.grantedLimitBrl) || 0;
      reserveHeld += Number(cs.reserveHeldBrl) || 0;
      plannedCredit += Number(cs.plannedCreditAmount) || 0;
      issuerAccountCount += Number(cs.issuerAccountCount) || 0;
      continue;
    }

    // List without credit_summary: fall back to planned + reserve held only.
    plannedCredit += Number(program?.plannedCreditAmount) || 0;
    reserveHeld += Number(program?.reserve?.held) || 0;
  }

  if (issuerAccountCount === 0) {
    return {
      ...EMPTY_BAAS_HOME_CREDIT_METRICS,
      currency: displayCurrency,
      plannedCreditAmount: convert(roundMoney(plannedCredit)),
    };
  }

  totalLimit = roundMoney(totalLimit);
  runningCredit = roundMoney(runningCredit);
  grantedLimit = roundMoney(grantedLimit);
  reserveHeld = roundMoney(reserveHeld);
  plannedCredit = roundMoney(plannedCredit);

  const utilizationRate =
    totalLimit > 0 ? roundRate((runningCredit / totalLimit) * 100) : 0;
  const collateralizedRate =
    totalLimit > 0 ? Math.min(100, roundRate((reserveHeld / totalLimit) * 100)) : 0;
  const unsecuredRate = roundRate(Math.max(0, 100 - collateralizedRate));
  const netExposure = roundMoney(runningCredit * (unsecuredRate / 100));
  const runningUnsecured = netExposure;
  const runningCollateralized = roundMoney(Math.max(0, runningCredit - runningUnsecured));
  const availableLimit = roundMoney(Math.max(0, totalLimit - runningCredit));

  return {
    currency: displayCurrency,
    totalLimitBrl: convert(totalLimit),
    runningCreditBrl: convert(runningCredit),
    utilizationRate,
    unsecuredGrantedBrl: convert(runningUnsecured),
    netExposureBrl: convert(netExposure),
    collateralDepositedBrl: convert(reserveHeld),
    runningCollateralizedBrl: convert(runningCollateralized),
    runningUnsecuredBrl: convert(runningUnsecured),
    availableLimitBrl: convert(availableLimit),
    collateralizedRate,
    unsecuredRate,
    grantedLimitBrl: convert(grantedLimit),
    reserveHeldBrl: convert(reserveHeld),
    plannedCreditAmount: convert(plannedCredit),
    issuerAccountCount,
  };
};

const CARDS_VOLUME_POINT_COUNT = 30;

const buildLast30DayLabels = () => {
  const labels = [];
  const today = new Date();

  for (let offset = CARDS_VOLUME_POINT_COUNT - 1; offset >= 0; offset -= 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - offset);
    labels.push(
      new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit" }).format(date),
    );
  }

  return labels;
};

/** Empty volume series until authorization / cards volume API exists. */
export const EMPTY_BAAS_HOME_CARDS_VOLUME_CHART = {
  labels: buildLast30DayLabels(),
  values: Array.from({ length: CARDS_VOLUME_POINT_COUNT }, () => 0),
  changePercent: 0,
};

/** @deprecated Use EMPTY_BAAS_HOME_CARDS_VOLUME_CHART. */
export const MOCK_BAAS_HOME_CARDS_VOLUME_CHART = EMPTY_BAAS_HOME_CARDS_VOLUME_CHART;

/**
 * @deprecated Prefer aggregateBaasProgramsCardsMetrics(programs). Returns zeros.
 */
export const getMockBaasHomeCardsMetrics = () => ({ ...EMPTY_BAAS_HOME_CARDS_METRICS });

/**
 * @deprecated Prefer aggregateBaasProgramsCreditMetrics(programs). Returns zeros.
 */
export const getMockBaasHomeCreditMetrics = (instanceId, currency = "BRL") => {
  void instanceId;
  return {
    ...EMPTY_BAAS_HOME_CREDIT_METRICS,
    currency: normalizeBaasHomeCurrency(currency),
  };
};

export const getMockBaasHomeCardsVolumeChart = (currency = "BRL") => {
  const displayCurrency = normalizeBaasHomeCurrency(currency);
  const values = EMPTY_BAAS_HOME_CARDS_VOLUME_CHART.values.map((value) =>
    convertBaasHomeAmountFromBrl(value, displayCurrency),
  );

  return {
    labels: EMPTY_BAAS_HOME_CARDS_VOLUME_CHART.labels,
    values,
    changePercent: 0,
    periodTotal: 0,
    currency: displayCurrency,
  };
};

const HOURLY_VOLUME_PATTERN = [
  0.032, 0.028, 0.025, 0.022, 0.02, 0.024, 0.031, 0.038, 0.042, 0.045, 0.048, 0.046, 0.044,
  0.043, 0.045, 0.047, 0.05, 0.052, 0.048, 0.044, 0.041, 0.039, 0.036, 0.034,
];

const HOURLY_VOLUME_PATTERN_EFX = [
  0.028, 0.026, 0.024, 0.022, 0.021, 0.023, 0.029, 0.034, 0.038, 0.041, 0.044, 0.043, 0.042,
  0.04, 0.042, 0.045, 0.048, 0.05, 0.046, 0.043, 0.04, 0.038, 0.035, 0.032,
];

const HOURLY_VOLUME_PATTERN_STABLES = [
  0.03, 0.027, 0.025, 0.023, 0.021, 0.024, 0.03, 0.036, 0.04, 0.043, 0.046, 0.044, 0.042, 0.041,
  0.043, 0.045, 0.048, 0.05, 0.047, 0.043, 0.04, 0.038, 0.035, 0.033,
];

const scaleHourlyVolume = (total, pattern = HOURLY_VOLUME_PATTERN) => {
  const patternSum = pattern.reduce((sum, value) => sum + value, 0);
  return pattern.map((value) => Math.round((value / patternSum) * total));
};

const DAILY_VOLUME_PATTERN = [
  0.92, 1.04, 0.98, 1.08, 1.12, 1.05, 1.1, 0.97, 1.06, 1.14, 1.02, 1.09, 1.11, 0.99, 1.07,
  1.13, 1.01, 1.08, 1.15, 1.03, 1.1, 0.96, 1.05, 1.12, 1.04, 1.09, 1.16, 1.0, 1.07, 1.11,
];

const scaleDailyVolume = (total, pointCount) => {
  const pattern = Array.from({ length: pointCount }, (_, index) => {
    const base = DAILY_VOLUME_PATTERN[index % DAILY_VOLUME_PATTERN.length];
    const wave = 1 + Math.sin(index / 2.4) * 0.04;
    return base * wave;
  });
  const patternSum = pattern.reduce((sum, value) => sum + value, 0);
  return pattern.map((value) => Math.round((value / patternSum) * total));
};

const buildProcessingVolumeSparkline = (total, dateRange, pattern = HOURLY_VOLUME_PATTERN) => {
  const granularity = getProcessingSparklineGranularity(dateRange);

  if (granularity === "hourly") {
    return scaleHourlyVolume(total, pattern);
  }

  const pointCount = getProcessingRangeDayCount(dateRange);
  return scaleDailyVolume(total, pointCount);
};

const scaleProcessingCount = (baseCount, dateRange) => {
  const multiplier = getProcessingVolumeMultiplier(dateRange);
  return Math.round(baseCount * multiplier);
};

const MOCK_PROCESSING_RAILS_BRL = {
  pix: {
    volume24hBrl: 18_420_000,
    txCount24h: 12480,
    successRate: 99.4,
    currency: "BRL",
  },
  efx: {
    volume24hBrl: 6_850_000,
    txCount24h: 318,
    successRate: 98.1,
    primaryPair: "USD/BRL",
  },
  stables: {
    volumeUsdc24hBrl: 1_940_000,
    volumeUsdt24hBrl: 1_300_000,
    txCount24h: 892,
    successRate: 99.8,
    assets: ["USDC", "USDT"],
  },
};

export const getMockBaasHomeProcessingRails = (
  currency = "BRL",
  dateRange = getDefaultProcessingDateRange(),
) => {
  const displayCurrency = normalizeBaasHomeCurrency(currency);
  const convert = (amountBrl) => convertBaasHomeAmountFromBrl(amountBrl, displayCurrency);
  const volumeMultiplier = getProcessingVolumeMultiplier(dateRange);

  const pixVolumeBrl = Math.round(MOCK_PROCESSING_RAILS_BRL.pix.volume24hBrl * volumeMultiplier);
  const efxVolumeBrl = Math.round(MOCK_PROCESSING_RAILS_BRL.efx.volume24hBrl * volumeMultiplier);
  const stablesUsdcVolumeBrl = Math.round(
    MOCK_PROCESSING_RAILS_BRL.stables.volumeUsdc24hBrl * volumeMultiplier,
  );
  const stablesUsdtVolumeBrl = Math.round(
    MOCK_PROCESSING_RAILS_BRL.stables.volumeUsdt24hBrl * volumeMultiplier,
  );
  const stablesVolumeBrl = stablesUsdcVolumeBrl + stablesUsdtVolumeBrl;

  const pixTrendVolume = buildProcessingVolumeSparkline(pixVolumeBrl, dateRange);
  const efxTrendVolumeBrl = buildProcessingVolumeSparkline(
    efxVolumeBrl,
    dateRange,
    HOURLY_VOLUME_PATTERN_EFX,
  );
  const stablesTrendVolumeBrl = buildProcessingVolumeSparkline(
    stablesVolumeBrl,
    dateRange,
    HOURLY_VOLUME_PATTERN_STABLES,
  );

  return {
    currency: displayCurrency,
    dateRange,
    pix: {
      ...MOCK_PROCESSING_RAILS_BRL.pix,
      volume: pixVolumeBrl,
      volume24h: pixVolumeBrl,
      trendVolume: pixTrendVolume,
      hourlyVolume: pixTrendVolume,
      txCount: scaleProcessingCount(MOCK_PROCESSING_RAILS_BRL.pix.txCount24h, dateRange),
      txCount24h: scaleProcessingCount(MOCK_PROCESSING_RAILS_BRL.pix.txCount24h, dateRange),
      displayCurrency: "BRL",
    },
    efx: {
      ...MOCK_PROCESSING_RAILS_BRL.efx,
      volume: convert(efxVolumeBrl),
      volume24h: convert(efxVolumeBrl),
      trendVolume: efxTrendVolumeBrl.map((value) => convert(value)),
      hourlyVolume: efxTrendVolumeBrl.map((value) => convert(value)),
      txCount: scaleProcessingCount(MOCK_PROCESSING_RAILS_BRL.efx.txCount24h, dateRange),
      txCount24h: scaleProcessingCount(MOCK_PROCESSING_RAILS_BRL.efx.txCount24h, dateRange),
      displayCurrency,
      primaryPair:
        displayCurrency === "EUR"
          ? "EUR/BRL"
          : displayCurrency === "USDC"
            ? "USDC/BRL"
            : "USD/BRL",
    },
    stables: {
      ...MOCK_PROCESSING_RAILS_BRL.stables,
      volumeUsdc: convertBaasHomeAmountFromBrl(stablesUsdcVolumeBrl, "USDC"),
      volumeUsdt: convertBaasHomeAmountFromBrl(stablesUsdtVolumeBrl, "USDT"),
      volume24hUsdc: convertBaasHomeAmountFromBrl(stablesUsdcVolumeBrl, "USDC"),
      volume24hUsdt: convertBaasHomeAmountFromBrl(stablesUsdtVolumeBrl, "USDT"),
      trendVolume: stablesTrendVolumeBrl,
      hourlyVolume: stablesTrendVolumeBrl,
      txCount: scaleProcessingCount(MOCK_PROCESSING_RAILS_BRL.stables.txCount24h, dateRange),
      txCount24h: scaleProcessingCount(MOCK_PROCESSING_RAILS_BRL.stables.txCount24h, dateRange),
      assets: MOCK_PROCESSING_RAILS_BRL.stables.assets,
    },
  };
};

export const sumCardProgramGuarantees = (instanceId, programs = null) => {
  // Home KPIs still use sync snapshot; pass fetched programs when available.
  // Sync mock list is empty — collateral stays 0 until home loads programs async.
  const list = Array.isArray(programs) ? programs : getMockBaasCardPrograms(instanceId) ?? [];

  return list.reduce((sum, program) => {
    // Collateral deposited = program reserve allocation, not planned required guarantee.
    const amount = Number(program.reserve?.balance ?? program.guaranteeAmount);
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
};

export const getBaasHomeApiSnapshotMetrics = () => {
  const { totalRequests, successRate, latencyP95Ms, serverError5xx } = getMockBaasApiMetrics();
  return { totalRequests, successRate, latencyP95Ms, serverError5xx };
};
